import { getUnitInfoData } from 'api/simulation/unitInfoDataApi';
import { put, takeLatest, fork, select } from 'redux-saga/effects';
import * as Sentry from '@sentry/react';
import { getUnitInfoDataSuccess } from './';
import { GET_UNIT_INFO_DATA_REQUEST } from './types';
import { addMessage } from 'store/snackbar/reducer';
import { errorMessage } from 'containers/Snackbar/snackbarMessages';
import { unitInfoDataCache } from 'util/cache/unitInfoDataCache';
import { getIsOnline } from 'store/connection/selectors';

function* loadFromCacheFirst() {
  try {
    const cachedData: Awaited<ReturnType<typeof unitInfoDataCache.get>> =
      yield unitInfoDataCache.get();

    if (cachedData) {
      yield put(getUnitInfoDataSuccess(cachedData));
    }

    return !!cachedData;
  } catch (err) {
    Sentry.captureException(err, {
      tags: { saga: 'unitInfoData', operation: 'loadFromCacheFirst' },
      level: 'error',
    });
    return false;
  }
}

function* fetchFromAPIAndCache() {
  try {
    const { parsedBody } = yield getUnitInfoData();
    yield put(getUnitInfoDataSuccess(parsedBody));
    yield unitInfoDataCache.set(parsedBody);
  } catch (err) {
    Sentry.captureException(err, {
      tags: { saga: 'unitInfoData' },
    });
    const cachedData: Awaited<ReturnType<typeof unitInfoDataCache.get>> =
      yield unitInfoDataCache.get();
    if (!cachedData) {
      yield put(addMessage(errorMessage('Failed to fetch application data')));
    }
  }
}

function* getUnitInfoDataRequest() {
  const hadCache: boolean = yield loadFromCacheFirst();
  const isOnline: boolean = yield select(getIsOnline);

  if (!isOnline) {
    return;
  }

  if (hadCache) {
    yield fork(fetchFromAPIAndCache);
  } else {
    yield fetchFromAPIAndCache();
  }
}

function* unitInfoDataSagas() {
  yield takeLatest(GET_UNIT_INFO_DATA_REQUEST, getUnitInfoDataRequest);
}

export default unitInfoDataSagas;
